import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import DataSourcePanel from './DataSourcePanel';
import KPICards from './KPICards';
import CandidatesList from './CandidatesList';
// AI Query feature disabled
// import QueryForm from './QuerySystem/QueryForm';
// import QueryResult from './QuerySystem/QueryResult';
import PipelineChart from './charts/PipelineChart';
import TimeToHireChart from './charts/TimeToHireChart';
import TimeToFillChart from './charts/TimeToFillChart';
import SkillsAnalysisChart from './charts/SkillsAnalysisChart';
import SourceEffectivenessChart from './charts/SourceEffectivenessChart';
import QuarterlyHiringChart from './charts/QuarterlyHiringChart';
import SalaryRangeChart from './charts/SalaryRangeChart';
import CandidateLocationChart from './charts/CandidateLocationChart';
import ClientHiringChart from './charts/ClientHiringChart';
import FeedbackScoreChart from './charts/FeedbackScoreChart';
import TimeToHireTrendChart from './charts/TimeToHireTrendChart';
import NPSScoreChart from './charts/NPSScoreChart';
import RoleSuccessRateChart from './charts/RoleSuccessRateChart';
import SeniorityDistributionChart from './charts/SeniorityDistributionChart';
import RoleDemandTrendChart from './charts/RoleDemandTrendChart';
import TimeToHireByRoleChart from './charts/TimeToHireByRoleChart';
import FilterPanel from './FilterPanel';
import { PermissionGate } from './ProtectedRoute';
import OpenRoles from './openRoles/OpenRoles';
import WeeklyReports from './WeeklyReports/WeeklyReports';
import { fetchData, authorizeAndAccessScript } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/roles';
// AI Query feature disabled
// import { processQueryWithGemini, getQuerySuggestions } from '../utils/geminiService';

// Chart descriptions for recruiters
const chartDescriptions = {
  pipelineChart: {
    title: 'Hire Metrics',
    description: 'Displays simplified hiring statistics showing the number of hired vs rejected candidates. Focus on key hiring outcomes to track overall recruitment success rates.'
  },
  timeToHireChart: {
    title: 'Time to Hire by Department',
    description: 'Displays the average days it takes to hire candidates for different departments. Use this to set realistic timelines for hiring managers and identify which departments need a more streamlined process.'
  },
  timeToFillChart: {
    title: 'Time to Fill by Department',
    description: 'Shows the average time from when a position opens until an offer is accepted for different departments. This metric helps identify departments that take longer to fill and may need additional sourcing strategies or process improvements.'
  },
  skillsAnalysisChart: {
    title: 'Top Departments Hired',
    description: 'Shows which departments have the highest number of successful hires. Use this to identify departments with active hiring needs and track recruitment success across different business areas.'
  },
  sourceEffectivenessChart: {
    title: 'Source Effectiveness',
    description: 'Compares which recruitment channels bring in the most candidates who get hired. Focus your time and budget on sources that deliver the best return on investment.'
  },
  quarterlyHiringChart: {
    title: 'Quarterly Hiring Trends',
    description: 'Tracks how many candidates you\'ve hired each quarter. Identify seasonal patterns to better plan your recruitment activities and resource allocation throughout the year.'
  },
  salaryRangeChart: {
    title: 'Salary Range by Department & Level',
    description: 'Shows the minimum, maximum, and average salary expectations across different departments and seniority levels. Use this data to ensure your offers are competitive and align with market rates.'
  },
  candidateLocationChart: {
    title: 'Candidate Locations',
    description: 'Maps where your candidates are located geographically. This insight helps you develop targeted regional recruitment strategies and assess remote work possibilities.'
  },
  clientHiringChart: {
    title: 'Client Hiring Activity',
    description: 'Shows completed hiring outcomes across different clients. Compare hired vs rejected candidates to assess client hiring success rates and identify high-performing partnerships.'
  },
  feedbackScoreChart: {
    title: 'Satellite Recommendation Score',
    description: 'Displays how likely clients are to recommend Satellite Talent to others (0-10 scale). This key metric measures client satisfaction and loyalty, helping you track the overall health of client relationships.'
  },
  timeToHireTrendChart: {
    title: 'Time-to-Hire Trends by Quarter',
    description: 'Tracks if your hiring process is getting faster or slower over time. Use this to measure the impact of process improvements and set benchmarks for recruitment efficiency.'
  },
  npsScoreChart: {
    title: 'Net Promoter Score (NPS) - TP Ratings',
    description: 'Shows NPS distribution based on Talent Partner (TP) rating numbers from client feedback. Use this to measure client satisfaction with individual TP performance and overall service quality.'
  },
  roleSuccessRateChart: {
    title: 'Role Success Rates',
    description: 'Shows the hiring success rate for different roles, helping identify which positions have the highest conversion rates from candidate to hire.'
  },
  seniorityDistributionChart: {
    title: 'Seniority Level Distribution',
    description: 'Displays the distribution of hired candidates across different experience levels (Junior, Mid, Senior, Lead), showing your hiring focus areas.'
  },
  roleDemandTrendChart: {
    title: 'Role Demand Trends',
    description: 'Tracks the popularity of different roles over time, helping identify emerging hiring trends and seasonal patterns in role demand.'
  },
  timeToHireByRoleChart: {
    title: 'Time to Hire by Role',
    description: 'Shows average hiring time across different roles, helping identify which positions take longer to fill and may need process improvements.'
  }
};

const Dashboard = () => {
  const location = useLocation();
  const { user } = useAuth();
  const canViewTPReports = user && hasPermission(user, PERMISSIONS.MANAGE_USERS);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDataSources, setShowDataSources] = useState(false);
  const [candidatesData, setCandidatesData] = useState([]);
  const [npsData, setNpsData] = useState([]);
  const [salaryData, setSalaryData] = useState([]);
  const [dataSource, setDataSource] = useState('google-sheets');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeklyReportsData, setWeeklyReportsData] = useState([]);
  const [weeklyReportsLoading, setWeeklyReportsLoading] = useState(false);
  // AI Query feature disabled
  // const [query, setQuery] = useState('');
  // const [queryResult, setQueryResult] = useState(null);
  // const [isProcessingQuery, setIsProcessingQuery] = useState(false);
  // const [suggestedQueries, setSuggestedQueries] = useState([]);
  
  // Filter states
  const [clientFilter, setClientFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [availableClients, setAvailableClients] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  
  // Fetch weekly reports when tab is first activated
  useEffect(() => {
    if (activeTab === 'tp-reports' && weeklyReportsData.length === 0 && !weeklyReportsLoading) {
      setWeeklyReportsLoading(true);
      fetchData('weekly-reports')
        .then(d => setWeeklyReportsData(d || []))
        .catch(err => console.error('Failed to fetch weekly reports:', err))
        .finally(() => setWeeklyReportsLoading(false));
    }
  }, [activeTab]);

  // Handle URL tab parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const validTabs = ['overview', 'candidates', 'open-roles', 'analytics'];
    if (canViewTPReports) validTabs.push('tp-reports');
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search, canViewTPReports]);

  // Google Apps Script URL
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzvrUbnOo7qknE3lAwtGAhzTZiaIrydzEsi0gX0ZimKmDS6wylS_j_F8Yt1l5320VZEJg/exec';
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data types
        try {
          const candidatesData = await fetchData('candidates');
          setCandidatesData(candidatesData);
          setDataSource(prev => ({ ...prev, candidates: 'Google Sheets' }));
          console.log('Loaded candidates data:', candidatesData.length, 'records');
        } catch (err) {
          console.error('Failed to fetch candidates data:', err);
          setDataSource(prev => ({ ...prev, candidates: 'CSV Fallback' }));
        }

        try {
          const npsData = await fetchData('nps');
          setNpsData(npsData);
          setDataSource(prev => ({ ...prev, nps: 'Google Sheets' }));
          console.log('Loaded NPS data:', npsData.length, 'records');
        } catch (err) {
          console.error('Failed to fetch NPS data:', err);
          setDataSource(prev => ({ ...prev, nps: 'CSV Fallback' }));
        }

        try {
          const salaryData = await fetchData('salary');
          setSalaryData(salaryData);
          setDataSource(prev => ({ ...prev, salary: 'Google Sheets' }));
          console.log('Loaded salary data:', salaryData.length, 'records');
        } catch (err) {
          console.error('Failed to fetch salary data:', err);
          setDataSource(prev => ({ ...prev, salary: 'CSV Fallback' }));
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error in fetchAllData:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);
  
  // Extract filters when data is loaded
  useEffect(() => {
    if (candidatesData.length > 0) {
      // AI Query feature disabled
      // generateQuerySuggestions();
      
      // Extract unique clients and departments for filters
      const clients = [...new Set(candidatesData.map(candidate => candidate.client).filter(Boolean))].sort();
      const departments = [...new Set(candidatesData.map(candidate => candidate.department || candidate.role).filter(Boolean))].sort();
      
      setAvailableClients(clients);
      setAvailableDepartments(departments);
    }
  }, [candidatesData]);
  
  // Set fallback data if any API calls fail
  useEffect(() => {
    if (error) {
      // Set fallback data for candidates if needed
      if (!candidatesData.length) {
        setCandidatesData([]);
      }
      
      // Set fallback data for NPS if needed
      if (!npsData.length) {
        setNpsData([]);
      }
      
      // Set fallback data for salary if needed
      if (!salaryData.length) {
        setSalaryData([]);
      }
    }
    
    if (npsData.length === 0) {
      setNpsData([
        { client: 'TechCorp', quarter: 'Q1 2024', averageScore: 8.5, individualScores: [8, 9, 10, 7, 8] },
        { client: 'DataPrime', quarter: 'Q1 2024', averageScore: 9.0, individualScores: [9, 10, 8, 9, 9] },
        { client: 'DigiTech', quarter: 'Q2 2024', averageScore: 8.8, individualScores: [9, 9, 10, 8, 8] }
      ]);
      setDataSource(prev => ({ ...prev, nps: 'Fallback Mock Data' }));
    }
    
    if (salaryData.length === 0) {
      setSalaryData([
        { role: 'Software Engineer', level: 'Junior', location: 'Berlin', minSalary: 45000, maxSalary: 55000, currency: 'EUR' },
        { role: 'Software Engineer', level: 'Mid', location: 'Berlin', minSalary: 55000, maxSalary: 75000, currency: 'EUR' },
        { role: 'Software Engineer', level: 'Senior', location: 'Berlin', minSalary: 75000, maxSalary: 95000, currency: 'EUR' },
        { role: 'Software Engineer', level: 'Lead', location: 'Berlin', minSalary: 90000, maxSalary: 120000, currency: 'EUR' },
        { role: 'Data Scientist', level: 'Junior', location: 'Munich', minSalary: 48000, maxSalary: 58000, currency: 'EUR' },
        { role: 'Data Scientist', level: 'Mid', location: 'Munich', minSalary: 58000, maxSalary: 78000, currency: 'EUR' },
        { role: 'Data Scientist', level: 'Senior', location: 'Munich', minSalary: 78000, maxSalary: 98000, currency: 'EUR' },
        { role: 'Data Scientist', level: 'Lead', location: 'Munich', minSalary: 95000, maxSalary: 125000, currency: 'EUR' }
      ]);
      setDataSource(prev => ({ ...prev, salary: 'Fallback Mock Data' }));
    }
  }, [error, candidatesData.length, npsData.length, salaryData.length]);

  // AI Query feature disabled
  /*
  // Event handlers
  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };
  */

  // AI Query feature disabled
  /*
  const handleSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
    processQuery(sampleQuery);
  };
  */

  // AI Query feature disabled
  /*
  const handleQuerySubmit = (e) => {
    e.preventDefault();
    processQuery(query);
  };
  */


  // AI Query feature disabled
  /*
  // Process natural language query using Gemini
  const processQuery = async (queryText) => {
    if (!queryText.trim()) return;
    
    setIsProcessingQuery(true);
    setQueryResult(null);
    
    try {
      // Process the query using Gemini API
      const result = await processQueryWithGemini(queryText, candidatesData, npsData, salaryData);
      
      // Set the query result
      setQueryResult(result);
    } catch (error) {
      console.error('Error processing query:', error);
      setQueryResult({
        type: 'text',
        value: null,
        text: `Sorry, I couldn't process that query: ${error.message}`
      });
    } finally {
      setIsProcessingQuery(false);
    }
  };
  */

  // AI Query feature disabled
  /*
  // Generate query suggestions based on the available data
  const generateQuerySuggestions = async () => {
    try {
      const suggestions = await getQuerySuggestions(candidatesData);
      setSuggestedQueries(suggestions);
    } catch (error) {
      console.error('Error generating query suggestions:', error);
    }
  };
  */
  
  // Filter candidates based on client and department filters
  const getFilteredCandidates = () => {
    return candidatesData.filter(candidate => {
      const matchesClient = clientFilter === '' || (candidate.client && candidate.client === clientFilter);
      const candidateDepartment = candidate.department || candidate.role || '';
      const matchesDepartment = departmentFilter === '' || candidateDepartment === departmentFilter;
      return matchesClient && matchesDepartment;
    });
  };
  
  // Handle filter changes
  const handleClientFilterChange = (client) => {
    setClientFilter(client);
  };
  
  const handleDepartmentFilterChange = (department) => {
    setDepartmentFilter(department);
  };
  
  const handleClearFilters = () => {
    setClientFilter('');
    setDepartmentFilter('');
  };

  // KPI Card click handlers
  const handleTotalHiresClick = () => {
    setActiveTab('candidates');
  };

  const handleAvgTimeToHireClick = () => {
    // Scroll to Time to Hire charts section
    setTimeout(() => {
      const timeToHireElement = document.querySelector('[data-chart="timeToHire"]');
      if (timeToHireElement) {
        timeToHireElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleAvgNpsScoreClick = () => {
    // Scroll to NPS charts section
    setTimeout(() => {
      const npsElement = document.querySelector('[data-chart="nps"]');
      if (npsElement) {
        npsElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center fade-in">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
          <p className="text-xl font-medium text-gray-700">Loading dashboard data...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the latest recruitment analytics</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center max-w-lg fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-6">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Error Loading Data</h2>
          <p className="text-gray-600 mb-4 px-4">{error}</p>
          <p className="text-gray-600 mb-6 px-4">Using fallback data for demonstration purposes.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-primary px-6 py-3 rounded-lg font-medium inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }


  // Get filtered candidates based on current filters
  const filteredCandidates = getFilteredCandidates();

  // Quarterly hire stats for standup banner
  const now = new Date();
  const currentQ = Math.floor((now.getMonth() + 3) / 3);
  const currentQuarterLabel = `Q${currentQ} ${now.getFullYear()}`;
  let lastQ = currentQ - 1;
  let lastQYear = now.getFullYear();
  if (lastQ < 1) { lastQ += 4; lastQYear -= 1; }
  const lastQuarterLabel = `Q${lastQ} ${lastQYear}`;
  const hiredCandidates = filteredCandidates.filter(c => c.stage === 'Hired');
  const hiresThisQuarter = hiredCandidates.filter(c => c.quarter === currentQuarterLabel).length;
  const hiresLastQuarter = hiredCandidates.filter(c => c.quarter === lastQuarterLabel).length;
  const quarterDelta = hiresThisQuarter - hiresLastQuarter;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        onShowDataSources={() => setShowDataSources(!showDataSources)}
        showDataSources={showDataSources}
      />
      
      
      {/* Data Source Panel */}
      <DataSourcePanel 
        showDataSources={showDataSources} 
        setShowDataSources={setShowDataSources} 
        dataSource={dataSource} 
        GOOGLE_SCRIPT_URL={GOOGLE_SCRIPT_URL} 
        authorizeAndAccessScript={authorizeAndAccessScript} 
      />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Filter Panel - Only show on overview and candidates tabs */}
        {(activeTab === 'overview' || activeTab === 'candidates') && (
          <FilterPanel 
            clients={availableClients}
            departments={availableDepartments}
            selectedClient={clientFilter}
            selectedDepartment={departmentFilter}
            onClientChange={handleClientFilterChange}
            onDepartmentChange={handleDepartmentFilterChange}
            onClearFilters={handleClearFilters}
            totalCandidates={candidatesData.length}
            filteredCandidates={filteredCandidates.length}
          />
        )}
        {activeTab === 'overview' ? (
          <div className="fade-in">
            {/* Standup Banner — Hires This Quarter */}
            <div
              className="rounded-2xl px-6 py-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 slide-in-up"
              style={{
                background: 'linear-gradient(135deg, #4841F0 0%, #3730c8 100%)',
                boxShadow: '0 4px 24px rgba(72,65,240,0.25)',
                animationDelay: '0ms'
              }}
            >
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-white/15 p-3 flex-shrink-0">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Standup Snapshot</p>
                  <p className="text-white font-bold text-xl" style={{fontFamily: "'Poppins', sans-serif"}}>
                    {hiresThisQuarter} {hiresThisQuarter === 1 ? 'hire' : 'hires'} this quarter
                    <span className="ml-2 text-white/70 font-normal text-base">({currentQuarterLabel})</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {hiresLastQuarter > 0 && (
                  <div className="text-right">
                    <p className="text-white/60 text-xs">vs last quarter</p>
                    <div className="flex items-center justify-end gap-1">
                      {quarterDelta > 0 ? (
                        <svg className="w-4 h-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      ) : quarterDelta < 0 ? (
                        <svg className="w-4 h-4 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      ) : null}
                      <span className={`text-sm font-semibold ${
                        quarterDelta > 0 ? 'text-emerald-300' : quarterDelta < 0 ? 'text-red-300' : 'text-white/70'
                      }`}>
                        {quarterDelta > 0 ? `+${quarterDelta}` : quarterDelta === 0 ? 'No change' : quarterDelta} {quarterDelta !== 0 ? `vs ${hiresLastQuarter} last quarter` : `(${hiresLastQuarter} last quarter)`}
                      </span>
                    </div>
                  </div>
                )}
                {hiresLastQuarter === 0 && (
                  <p className="text-white/50 text-xs">{lastQuarterLabel}: no data</p>
                )}
              </div>
            </div>

            {/* KPI Cards - Calculate KPIs based on filtered data and user permissions */}
            <KPICards 
              totalHires={hiredCandidates.length}
              hiresThisQuarter={hiresThisQuarter}
              currentQuarterLabel={currentQuarterLabel}
              avgTimeToHire={filteredCandidates.filter(c => c.stage === 'Hired' && c.timeToHire && c.timeToHire > 0).length > 0 ?
                Math.round(filteredCandidates.filter(c => c.stage === 'Hired' && c.timeToHire && c.timeToHire > 0)
                  .reduce((sum, c) => sum + (parseInt(c.timeToHire) || 0), 0) / 
                  filteredCandidates.filter(c => c.stage === 'Hired' && c.timeToHire && c.timeToHire > 0).length) : 32}
              avgNpsScore={npsData.filter(item => item.averageScore && parseFloat(item.averageScore) > 0).length > 0 ? 
                (npsData.filter(item => item.averageScore && parseFloat(item.averageScore) > 0)
                  .reduce((sum, item) => sum + parseFloat(item.averageScore), 0) / 
                  npsData.filter(item => item.averageScore && parseFloat(item.averageScore) > 0).length).toFixed(1) : '0.0'}
              avgSalaryExpectation={filteredCandidates.filter(c => c.salary && c.salary.amount && c.salary.amount > 0).length > 0 ? 
                Math.round(filteredCandidates.filter(c => c.salary && c.salary.amount && c.salary.amount > 0)
                  .reduce((sum, c) => sum + (parseFloat(c.salary.amount) || 0), 0) / 
                  filteredCandidates.filter(c => c.salary && c.salary.amount && c.salary.amount > 0).length) : 0}
              onTotalHiresClick={handleTotalHiresClick}
              onAvgTimeToHireClick={handleAvgTimeToHireClick}
              onAvgNpsScoreClick={handleAvgNpsScoreClick}
            />

            {/* Charts - Pass filtered data to all charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PipelineChart data={filteredCandidates} description={chartDescriptions.pipelineChart.description} />
              <div data-chart="timeToHire">
                <TimeToHireChart data={filteredCandidates} description={chartDescriptions.timeToHireChart.description} />
              </div>
              <TimeToFillChart data={filteredCandidates} description={chartDescriptions.timeToFillChart.description} />
              <SkillsAnalysisChart data={filteredCandidates} description={chartDescriptions.skillsAnalysisChart.description} />
              <SourceEffectivenessChart data={filteredCandidates} description={chartDescriptions.sourceEffectivenessChart.description} />
              <QuarterlyHiringChart data={filteredCandidates} description={chartDescriptions.quarterlyHiringChart.description} />
              <SalaryRangeChart data={filteredCandidates} description={chartDescriptions.salaryRangeChart.description} />
              <CandidateLocationChart data={filteredCandidates} description={chartDescriptions.candidateLocationChart.description} />
              <ClientHiringChart data={filteredCandidates} description={chartDescriptions.clientHiringChart.description} />
              
              {/* Admin-only: Individual client NPS feedback scores */}
              <PermissionGate requiredPermission="view_individual_client_nps">
                <FeedbackScoreChart data={npsData} description={chartDescriptions.feedbackScoreChart.description} />
              </PermissionGate>
              
              <TimeToHireTrendChart data={filteredCandidates} description={chartDescriptions.timeToHireTrendChart.description} />
              
              {/* Admin-only: NPS by individual Talent Partner ratings */}
              <PermissionGate requiredPermission="view_individual_client_nps">
                <div data-chart="nps">
                  <NPSScoreChart data={npsData} description={chartDescriptions.npsScoreChart.description} />
                </div>
              </PermissionGate>
              <RoleSuccessRateChart data={filteredCandidates} description={chartDescriptions.roleSuccessRateChart.description} />
              <SeniorityDistributionChart data={filteredCandidates} description={chartDescriptions.seniorityDistributionChart.description} />
              <RoleDemandTrendChart data={filteredCandidates} description={chartDescriptions.roleDemandTrendChart.description} />
              <TimeToHireByRoleChart data={filteredCandidates} description={chartDescriptions.timeToHireByRoleChart.description} />
            </div>
          </div>
        ) : activeTab === 'candidates' ? (
          <CandidatesList 
            candidatesData={filteredCandidates} 
          />
        ) : activeTab === 'open-roles' ? (
          <OpenRoles />
        ) : activeTab === 'tp-reports' && canViewTPReports ? (
          <WeeklyReports data={weeklyReportsData} isLoading={weeklyReportsLoading} />
        ) : (
          <div className="fade-in">
            {/* AI Query Tab - Disabled */}
            <div className="p-6 bg-white shadow-md rounded-lg mb-6">
              <h2 className="text-xl font-semibold mb-4">AI Query System</h2>
              <p className="text-gray-600">The AI query feature has been temporarily disabled.</p>
            </div>
            
            {/* 
            <QueryForm 
              query={query}
              handleQueryChange={handleQueryChange}
              handleQuerySubmit={handleQuerySubmit}
              handleSampleQuery={handleSampleQuery}
              isProcessingQuery={isProcessingQuery}
              suggestedQueries={suggestedQueries}
            />
            
            {queryResult && (
              <QueryResult 
                queryResult={queryResult} 
                candidatesData={candidatesData}
                handleCandidateClick={handleCandidateClick}
              />
            )}
            */}
          </div>
        )}
      </main>

    </div>
  );
};

export default Dashboard;
